import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useParams } from 'react-router-dom';
import { useSelector, useDispatch } from "react-redux";
import { setCustomerInfo } from 'redux/actions'
import {
  Content,
  ContentColumn,
  Container,
} from 'components/BookingMain/styles';
import GuestInformation from 'components/BookingMain/GuestInformation';
import Button from 'components/Button';
import Spinner from 'components/Spinner';
import GeneralRoomNotes from './components/GeneralRoomNotes';
import StatusHistory from './components/StatusHistory';
import RoomDetailsHeader from './components/RoomDetailsHeader';
import BookingList from './components/BookingList';
import { RoomDetailsContainer } from './styles';
import { GET_ROOM_BY_ROOM_NUMBER, UPDATE_BOOKING_BY_BOOKING_ID } from 'graphql/service';
import { useQuery, useMutation } from '@apollo/client';
import { debouncedErrorMessage } from 'utils/messageUtil';
import { message } from 'antd';
import TravelArrangements from 'components/BookingMain/TravelArrangements';

const RoomDetails = () => {

  const roomNumber = useParams().roomNumber
  const dispatch = useDispatch();
  const customerInfo = useSelector(
    (state) => state.customerInfoReducer.customerInfo
  );

  const [previousBookings, setPreviousBookings] = useState([])
  const [upcomingBookings, setUpcomingBookings] = useState([])
  const [roomNotes, setRoomNotes] = useState([])
  const [showedBooking, setShowedBooking] = useState({})
  const [customerStatus, setCustomerStatus] = useState('')
  const [customerInfoError, setCustomerInfoError] = useState({});

  const { data: roomData, loading, error } = useQuery(GET_ROOM_BY_ROOM_NUMBER, {
    variables: { roomNumber },
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  });

  const room = roomData?.roomByNumber
  const roomStatusHistories = room?.roomStatusHistories ?? []
  const roomBookings = room?.roomBookings ?? []
  const roomType = room?.roomType ?? ''
  const roomId = room?.roomId ?? ''

  useEffect(() => {
    if (room) {
      setRoomNotes(room.roomNotes || []);;
    }
  }, [room]);

  useEffect(() => {
    if (!error) return;

    if (!error.networkError) {
      let content = error.message;

      if (error.message.includes('Validation')) {
        content = JSON.stringify(
          error.graphQLErrors[0]?.extensions?.exception
        );
      }

      debouncedErrorMessage(content);
    }
  }, [error]);

  const [updateBookingByBooingId, { loading: updateLoading }] = useMutation(UPDATE_BOOKING_BY_BOOKING_ID, {
    onCompleted: (data) => {
      message.success('Updated successfully')
    },
    onError: (error) => {
      if (!error.networkError) {
        let content = error.message
        if(error.message.includes('Validation')) {
            content = JSON.stringify(error.graphQLErrors[0]?.extensions?.exception)
        }
        debouncedErrorMessage(content)
      }
    }
  })

  const status = roomStatusHistories[0]?.status

  useEffect(() => {
    if (roomBookings && roomBookings.length > 0) {
      const currentIndex = roomBookings?.findIndex((roomBooking) => roomBooking.isCurrent === true)
      const bookings = roomBookings.map((roomBooking) => roomBooking.booking)
      if (currentIndex !== -1) {
        if (status?.includes('OCCUPIED')) {
          const currentBooking = bookings[currentIndex]
          setShowedBooking(currentBooking)
          setCustomerStatus('CURRENT')
          dispatch(
            setCustomerInfo({
              fullName: currentBooking.fullName,
              email: currentBooking.email,
              phoneNumber: currentBooking.phoneNumber
            })
          );
        } else {
          const upcomingBooking = bookings[currentIndex - 1]
          const previousBooking = bookings[currentIndex]
          setShowedBooking(upcomingBooking)
          setCustomerStatus('UPCOMING')
          if(upcomingBooking) {
            dispatch(
              setCustomerInfo({
                fullName: upcomingBooking.fullName,
                email: upcomingBooking.email,
                phoneNumber: upcomingBooking.phoneNumber
              })
            );
          } else if(previousBooking) {
            setShowedBooking(previousBooking)
            setCustomerStatus('PREVIOUS')
            dispatch(
              setCustomerInfo({
                fullName: previousBooking.fullName,
                email: previousBooking.email,
                phoneNumber: previousBooking.phoneNumber
              })
            );
          }
        }
        const upcomingBookings = bookings.slice(0, currentIndex)
        const previousBookings = bookings.slice(currentIndex + 1)
        setPreviousBookings(previousBookings)
        setUpcomingBookings(upcomingBookings)
      } else {
        setPreviousBookings(bookings)
        setCustomerStatus('PREVIOUS')
        dispatch(
          setCustomerInfo({
            fullName: bookings[0]?.fullName || '',
            email: bookings[0]?.email || '',
            phoneNumber: bookings[0]?.phoneNumber || ''
          })
        );
      }
    }
  }, [roomBookings])

  if (loading) return <Spinner />;
  if (error) return <p>Error: {error.message}</p>;
  
  const handleSaveChanges = () => {
    if(loading) return
    updateBookingByBooingId({
      variables: {
        bookingId: showedBooking?.bookingId,
        input: {
          fullName: customerInfo.fullName,
          email: customerInfo.email,
          phoneNumber: customerInfo.phoneNumber,
        }
      }
    })
  }

  return (
    <>
      <Helmet>
        <title>Room Details - Rook Hotel PMS</title>
        <meta name="description" content="Room Details - Rook Hotel PMS" />
      </Helmet>
      <RoomDetailsContainer>
        <RoomDetailsHeader
          roomNumber={roomNumber}
          roomId={roomId}
          roomType={roomType}
          status={roomStatusHistories?.[0]?.status}
        />
        <Content
          additionalstyle={`
            margin-top: 0;
          `}
        >
          <ContentColumn>
            <Container>
              <GuestInformation
                mini
                customerStatus={customerStatus}
                customerInfoError={customerInfoError}
                setCustomerInfoError={setCustomerInfoError}
                btnInfo={
                  <Button
                    mainPage
                    success
                    onClick={handleSaveChanges}
                    data-loading={updateLoading}
                    additionalstyle={`
                      width: 113px;
                    `}
                  >
                    {updateLoading ? null: 'Save Changes'}
                  </Button>
                }
              />
            </Container>
            <Container>
              <TravelArrangements
                customerStatus={customerStatus}
                bookingId={showedBooking?.bookingId}
                checkInDate={showedBooking?.checkedInAt || showedBooking?.checkInDate}
                checkOutDate={showedBooking?.checkedOutAt || showedBooking?.checkOutDate}
                setCheckInDate={(e)=>setShowedBooking({...showedBooking, checkInDate: e})}
                setCheckOutDate={(e)=>setShowedBooking({...showedBooking, checkOutDate: e})}
              />
            </Container>
            <Container>
              <BookingList type="UPCOMING BOOKINGS" bookings={upcomingBookings} />
            </Container>
          </ContentColumn>
          <ContentColumn>
            <Container>
              <GeneralRoomNotes
                roomId={roomId}
                notes={roomNotes}
                setNotes={setRoomNotes}
              />
            </Container>
            <Container>
              <StatusHistory roomStatusHistories={roomStatusHistories} />
            </Container>
            <Container>
              <BookingList type="PREVIOUS BOOKINGS" bookings={previousBookings} />
            </Container>
          </ContentColumn>
        </Content>
      </RoomDetailsContainer>
      {/* <FooterBar /> */}
    </>
  );
};

export default RoomDetails;